import { 
  ChartType, 
  DurationFromType, 
  ChartInput 
} from 'src/services/graphql/Models';
import Filter from './Filter';
import { SortedUnitSensor } from 'src/shared/SortedUnitSensorData';
import { ChartData, ChartDataSets } from 'chart.js';
import TriggerableStat from './TriggerableStat';
import convert from 'convert-units';
import { Dictionary, DataClass, processArrayAsync } from 'src/shared/utility';
import UnitSensor from './UnitSensor';
import ChartConfiguration from './ChartConfiguration';
import ChartConfigurations from './ChartConfigurations';
import { Unit } from '../unit';
import { ChartDataResult } from 'src/container/Chart/renders/Shared';
import SensorType, { findSensorType } from 'src/stores/application/classes/SensorType';
import GetCommCache from 'src/shared/GetCommCache';

const enum ComparisionGroupingTypeEnum {
  unitGrouped,
  sensorGrouped
}

type NameGetter = (unit: Unit, unitSensor: UnitSensor) => string;
type ChartConfigurationGetter = (dataSetIndex: number, labelCount: number ) => any;

interface GroupDataResult {
  distinctSensor: Dictionary < Dictionary < number > >;
  labels: string[];
}

export default class Chart implements ChartInput, DataClass {
  clientId: string;
  typedId: string;
  name: string;
  chartType: ChartType;
  sensorType: string;
  measurementUnit: string;
  include?: Array< Filter | null > | null;
  exclude?: Array< Filter | null > | null;
  durationFromType: DurationFromType;
  durationFrom: string;
  comparisionGrouping: ComparisionGroupingTypeEnum;
  breakOnError?: boolean | null;
  geospread?: boolean | null;

  constructor ( from: any ) { 
    this.clientId = from.clientId;
    this.typedId = from.typedId;
    this.name = from.name;
    this.chartType = from.chartType;
    this.sensorType = from.sensorType;
    this.measurementUnit = from.measurementUnit;
    if (from.include && from.include.slice) {
      let include = from.include.slice();
      this.include = include.map(o => new Filter(o));
    } else {
      this.include = [];
    }
    if (from.exclude && from.exclude.slice) {
      let exclude = from.exclude.slice();
      this.exclude = exclude.map(o => new Filter(o));
    } else {
      this.exclude = [];
    }
    
    this.durationFromType = from.durationFromType;
    this.durationFrom = from.durationFrom;
    this.comparisionGrouping = ComparisionGroupingTypeEnum.unitGrouped;    
    this.breakOnError = from.breakOnError;
    this.geospread = from.geospread;
    // this.override = from.override;
  }

  /**
   * gets the actual duration for the chart
   */
  get actualDuration(): Date {
    let duration = Number(this.durationFrom);
    switch (this.durationFromType) {
      case DurationFromType.days:
        duration *= 86400000;
        break;
      case DurationFromType.hour:
        duration *= 3600000;
        break;
      case DurationFromType.minute:
        duration *= 60000;
        break;
      default:
        duration *= 1000;
    }

    return new Date(Date.now() - duration);
  }

  /**
   * Checkes if the unit sensor is included in the chart.
   */
  includesUnitSensor(unit: any, unitSensorAlias: any, sensorType: any) {
    const allIncludes = this.include.map(dat => dat.filterSensor(unitSensorAlias, unit));
    const allexcludes = this.exclude.map(dat =>  dat.filterSensor(unitSensorAlias, unit));
  
    const includeSum = allIncludes.reduce((a, b) => a + b, 0);
    const excludeSum = allexcludes.reduce((a, b) => a + b, 0);
    const result = ( 
      includeSum > excludeSum || ( 
        this.include.length  === this.exclude.length 
        && this.exclude.length === 0 
        
      )
    ) && this.sensorType === sensorType;

    return result;
  }

  parseAbsolute() {
    return {
      clientId: this.clientId,
      typedId: this.typedId,
      name: this.name,
      chartType: this.chartType,
      sensorType: this.sensorType,
      measurementUnit: this.measurementUnit,
      include: (this.include || []).map(o => o.parseAbsolute()),
      exclude: (this.exclude || []).map(o => o.parseAbsolute()),
      durationFromType: this.durationFromType,
      durationFrom: this.durationFrom,
      breakOnError: this.breakOnError,
      geospread: this.geospread
    };
  }

  getChartDataResult({ 
    commcaches, 
    unitsensors, 
    units,
    sensortypes,
    triggerableStats,
    override,
    commCacheItems,
    chartState
  }: any): Promise<ChartDataResult> {
    if (
        typeof commcaches === 'undefined' ||
        typeof unitsensors === 'undefined' ||
        typeof units === 'undefined' ||
        typeof sensortypes === 'undefined' ||
        typeof triggerableStats === 'undefined'
    ) {
      return new Promise((res, rej) => rej(new Error('getChartDataResult: undefined props')));
    }

    let chart = new Chart(this);
    
    if (typeof chartState === 'object') {
      Object.keys(chartState).forEach(key => {
        chart[key] = chartState[key];
      });
    }
    
    const unitSensorItems: UnitSensor[] = unitsensors.getParsedItems
        .filter(o => o.clientId === chart.clientId);
    const unitItems: Unit[] = units.getParsedItems;
    
    const sensortypeItems: SensorType[] = sensortypes.getParsedItems;
    const sensorTypeItem = sensortypeItems.find(o => o.typedId === chart.sensorType);
    if (typeof sensorTypeItem === 'undefined') {
      return new Promise((res, rej) => rej(new Error('getChartDataResult: undefined sensorTypeItem')));
    }
    
    // const commCacheItems: Array< Array < CommCache > > = commcaches.getParsedItems as any;
    const commCacheMap: any = {};
    const processComCacheMap  = this._processCommCacheMap.bind(
      this,
      { 
        commcaches, 
        sensorTypeItem,
        sensortypeItems,
        unitItems,
        triggerableStats,
        override,
        unitSensorItems,
        chart
      }
    );

    if (Array.isArray(commCacheItems)) {  
      return new Promise(res => {
        processArrayAsync(commCacheItems, (a, c1) => {
          return processArrayAsync(a as any[], (b, c2) => {
            const USN = b.unitId; 
            commCacheMap[USN] = commCacheMap[USN] || [];
            commCacheMap[USN].push(b);
          });
        }).then(() => {
          processComCacheMap(commCacheMap).then(res);
        });
      });
    } else {
      return new Promise((res, rej) => rej(new Error('getChartDataResult: undefined comm cache items')));
    }

  }

  getCommInputs({
    commcaches, 
    unitsensors, 
    units,
    sensortypes,
    override,
    chartState
  }: any) {

    if (
        typeof unitsensors === 'undefined' ||
        typeof units === 'undefined' ||
        typeof sensortypes === 'undefined'
    ) {
      return undefined;
    }
  
    let chart = new Chart(this);
    
    if (typeof chartState === 'object') {
      Object.keys(chartState).forEach(key => {
        chart[key] = chartState[key];
      });
    }
    
    const unitSensorItems: UnitSensor[] = unitsensors.getParsedItems
        .filter(o => o.clientId === chart.clientId);
    
    const sensortypeItems: SensorType[] = sensortypes.getParsedItems;
    const sensorTypeItem = sensortypeItems.find(o => o.typedId === chart.sensorType);
  
    if (
      override &&
      override.unitSensors
    ) {     
        let { 
            unitSensors,
            fromDate,
            toDate
        } = override;
        
        unitSensors.forEach(unitSensor => {
            commcaches.putCommCacheInput(new GetCommCache({
                unitId: unitSensor.unitId,
                typedId: unitSensor.typedId,
                clientId: unitSensor.clientId,
                sinceDate: fromDate,
                toTime: toDate,
                significantDifference: sensorTypeItem.significantTimeDifference
            }));
  
        });
    } else {
      let time = Number(chart.durationFrom);
  
      switch (chart.durationFromType) {
          case DurationFromType.days:
              time *= 24 * 60 * 60;
              break;
          case DurationFromType.hour:
              time *= 60 * 60;
              break;
          case DurationFromType.minute:
              time *= 60;
              break;
          default:
              break;
      }
      let fromTime = time * 1000;
      // this.duration = fromTime;
  
      time = Math.floor(Date.now() / 1000) - time;
  
      unitSensorItems.forEach(unitSensorItem =>  {
          commcaches.putCommCacheInput(new GetCommCache({
            unitId: unitSensorItem.unitId,
            typedId: unitSensorItem.typedId,
            clientId: unitSensorItem.clientId,
            fromTime,
            toPresent: true,
            significantDifference: sensorTypeItem.significantTimeDifference
          }));
      });
    } 
  }

  /**
   * converts the given sorted data into actual chart data used 
   * to render in the chart
   */
  getChartData(
    commUnit: string,
    sortedUnitSensors: SortedUnitSensor[], 
    triggerableStats: TriggerableStat[], 
    fromDate ?: Date,
    toDate ?: Date
  ): Promise<ChartData> {
    switch (this.chartType) {
      case ChartType.table:
        return this.getTableData(
          triggerableStats,
          sortedUnitSensors,
          commUnit
        );
      case ChartType.line:
        return this.getLineChartData(
          sortedUnitSensors,
          commUnit,
          fromDate,
          toDate
        );
      default:
        return this.getDefaultChartData(sortedUnitSensors, triggerableStats);
    }
  }

  private getTableData(
    triggerableStats: any,
    sortedUnitSensors: any[],
    commUnit: any
  ): Promise<ChartData> {
    return new Promise<ChartData>( res => {
      const labels = triggerableStats.map(stat => stat.name);
      processArrayAsync(
        sortedUnitSensors, 
        (sortedUnitSensor => { 
          const { unitSensor, unit } = sortedUnitSensor;
          let configurations = unitSensor.chartConfiguration;
          
          return {
            ...configurations,
            label: `${unitSensor.name}`,
            data: [ 
              ({
                x: 'id',
                y: unitSensor.typedId
              }),
              ({
                x: 'name',
                y: unitSensor.name,
              }),
              ({
                x: 'unitName',
                y: unit.name
              }),
              ({
                x: 'lastUpdate',
                y: unitSensor.lastUpdate,
              }),
              ...triggerableStats.map(triggerableStat => ({
                x: triggerableStat.typedId,
                y: convert(triggerableStat.getComparedStatVal(unitSensor))
                  .from(commUnit)
                  .to(this.measurementUnit)
              }))
            ]
          } as ChartDataSets;
        })
      ).then(datasets => {
        res({ labels, datasets } as ChartData);    
      });
      
  });

  }

  private getLineChartData(
    sortedUnitSensors: any[],
    commUnit: any,
    fromDate: any,
    toDate: any
  ): Promise<ChartData> {
    return new Promise<any> (res => {
      processArrayAsync(
        sortedUnitSensors,
        sortedUnitSensor => { 
          const { unitSensor, unit } = sortedUnitSensor;
          const configurations = unitSensor.chartConfiguration;
          const actualFrom = fromDate || this.actualDuration;
          const data = sortedUnitSensor.getTimeSeries(
            actualFrom,
            commUnit,
            this.measurementUnit,
            this.breakOnError,
            toDate
          );
          return {
            ...configurations,
            label: `${unitSensor.name}: ${unit.name}`,
            data,
          } as ChartDataSets;
        }
      ).then(datasets => res({ datasets }));
    });
  }

  private getDefaultChartData (
    records: SortedUnitSensor[],
    triggerableStats: TriggerableStat[],
    grouping?: ComparisionGroupingTypeEnum
  ): Promise<ChartData> {
    return new Promise(res => {
      let {distinctSensor, labels} = this.groupData(records, triggerableStats, grouping );
      let result: ChartData = {};
      let ColorGetter: ChartConfigurationGetter;
  
      result.labels = labels;
      
      switch (this.chartType) {
        case ChartType.pie:
        case ChartType.donut:
        case ChartType.polar:
          ColorGetter = (dataSetIndex: number, labelCount: number ) => {
            // generating the configurations
            let generatedColor: ChartConfiguration[] = [];
            for (let c = 0; c < labelCount; c ++) {
              generatedColor.push(new ChartConfiguration(undefined, c, labelCount));
            }
  
            return new ChartConfigurations(generatedColor);
          };
          break;
        default:
          ColorGetter = (dataSetIndex: number, labelCount: number ) => 
            new ChartConfiguration(undefined, dataSetIndex, labelCount);
          break;
      }
      
      result.datasets = [];
      let count = 0;
  
      for (let dataSetLabel in distinctSensor) {
        if (
          dataSetLabel &&
          dataSetLabel !== 'prototype' &&
          dataSetLabel !== '__proto__'
        ) {  
          let dataDictionary = distinctSensor[dataSetLabel];
          let dataArray = [];
  
          for (let c = 0; c < labels.length; c ++) {
            dataArray.push(dataDictionary[labels[c]]);
          }
  
          let currentColor = ColorGetter(count + 1, dataArray.length);
           
          count ++;
          result.datasets.push({
            ...currentColor,
            label: dataSetLabel,
            data: dataArray
          });
        }
      }
  
      res(result);
    });
  }

  private groupData(
    records: SortedUnitSensor[],
    triggerableStats: TriggerableStat[],
    grouping?: ComparisionGroupingTypeEnum
  ): GroupDataResult {
    let comparisionGrouping = grouping || this.comparisionGrouping;

    let result: GroupDataResult = {
      distinctSensor: {},
      labels: []
    };

    let groupNameGetter: NameGetter;
    let labelNameGetter: NameGetter;
    
    // let unitGroupedRecords: Array<any> = [];

    // records.forEach(record => {
    //   let unitGroupedIndex = unitGroupedRecords.findIndex(
    //     rec => rec.unit.typedId === record.Unit.typedId
    //   );

    //   if (
    //     unitGroupedIndex < 0 ||
    //     unitGroupedIndex > unitGroupedRecords.length
    //   ) {
    //     unitGroupedIndex = unitGroupedRecords.length;
    //     unitGroupedRecords.push({
    //       unit: record.Unit,
    //       record: [record]
    //     });
    //   } else {
    //     unitGroupedRecords[unitGroupedIndex].record.push(record);
    //   }
    // });

    switch (comparisionGrouping) {
      case ComparisionGroupingTypeEnum.unitGrouped:
        groupNameGetter = (unit: Unit, unitSensor: UnitSensor) => unit.name;
        labelNameGetter = (unit: Unit, unitSensor: UnitSensor) => unitSensor.name;
        break;

      case ComparisionGroupingTypeEnum.sensorGrouped:
        groupNameGetter = (unit: Unit, unitSensor: UnitSensor) => unitSensor.name;
        labelNameGetter = (unit: Unit, unitSensor: UnitSensor) => unit.name;
        break;
        
      default:
        groupNameGetter = (unit: Unit, unitSensor: UnitSensor) => 'Data';
        labelNameGetter = (unit: Unit, unitSensor: UnitSensor) => unitSensor.name;
        break;
    }

    records.forEach(record => {
      let groupName = groupNameGetter(record.unit, record.unitSensor);
      let labelName = labelNameGetter(record.unit, record.unitSensor);

      result = this.setData(result, groupName, labelName, record.unitSensor, triggerableStats);
    });

    return result;
  }

  private setData(
    {
      distinctSensor, 
      labels
    }: {
      distinctSensor: Dictionary<Dictionary<number>>, 
      labels: string[]
    }, 
    labelName: string, 
    groupName: string, 
    unitSensor: UnitSensor,
    triggerableStats: TriggerableStat[]
  ) {
    let getValue =  triggerableStats[0].getComparedStatVal.bind(triggerableStats[0]);
    if (!distinctSensor[groupName]) {
      distinctSensor[groupName] = {};
    }

    if (!labels.some(label => label === labelName)) {
      labels.push(labelName);  
    }

    distinctSensor[groupName][labelName] = getValue(unitSensor);

    return {distinctSensor, labels};
  }

  /**
   * Processes the given commcachemap into the sorted unit sensor
   */
  private _processCommCacheMap(
    { 
      commcaches, 
      sensorTypeItem,
      sensortypeItems,
      unitItems,
      triggerableStats,
      override,
      unitSensorItems,
      chart
    }: any,
    commCacheMap: any
  ): Promise<ChartDataResult> {
    return new Promise(res => {

      let _fromDate: any = undefined;
      let _toDate: any = undefined;
      let overridden = false;
      if (
        override &&
        override.unitSensors
      ) {     
        _fromDate = override.fromDate;
        _toDate = override.toDate;
        overridden = true;
      } 
      (
        overridden ? 
        this.processOverridenCommCacheMap(
          override,
          unitItems,
          commcaches,
          sensorTypeItem,
          commCacheMap
        ) : 
        this.processCommCacheMap(
          chart,
          unitSensorItems,
          unitItems,
          sensortypeItems,
          commcaches,
          commCacheMap,
          triggerableStats
        )
      ).then(({ts, sortedUnitSensors}) => {
        if (this.geospread) {
          const unitSensorGrouping = {};
          const unitGroupedId = {};
    
          sortedUnitSensors.forEach(sortedUnitSensor => { 
              const unitId = sortedUnitSensor.unit.typedId;
              unitSensorGrouping[unitId] = unitSensorGrouping[unitId] || [];
              unitSensorGrouping[unitId].push(sortedUnitSensor);
              unitGroupedId[unitId] = unitGroupedId[unitId] || sortedUnitSensor.unit;
          });

          Promise.all(
            Object.keys(unitSensorGrouping).map(unitId => new Promise<any>(
              _res => {
                chart.getChartData(
                  sensorTypeItem.commUnit,
                  unitSensorGrouping[unitId], 
                  ts, 
                  _fromDate,
                  _toDate
                ).then(data => _res({
                  unit: unitGroupedId[unitId],
                  data,
                  sortedUnitSensor: unitSensorGrouping[unitId]
                }));
              }
            ))
          ).then(result => res(result));
        } else {
          chart.getChartData(
            sensorTypeItem.commUnit,
            sortedUnitSensors, 
            ts,
            _fromDate,
            _toDate
          ).then(data => res({
            data,
            sortedUnitSensor: sortedUnitSensors
          }));
        }
      });
    });
  }
  private processOverridenCommCacheMap(
    override: any,
    unitItems: any,
    commcaches: any,
    sensorTypeItem: any,
    commCacheMap: any
  ) {
    const result: any = {};
    const ts = result.ts = [];
    const sortedUnitSensors = result.sortedUnitSensors = [];
    return new Promise((res, rej) => {
      let { 
        unitSensors,
        viewstat,
        fromDate,
        toDate
      } = override;
  
      ts.push(viewstat);
      
      // this.duration = toDate.getTime() - fromDate.getTime();
      processArrayAsync(
        unitSensors as any[],
        unitSensor => {
          let unitIndex = unitItems.findIndex(unitItem => 
              unitSensor.unitId === 
              unitItem.typedId.substring(0, unitSensor.unitId.length)
          );
          
          if (
              unitIndex < 0 || 
              unitIndex >= unitItems.length
          ) {
              console.error('Incorrect unitId');
              console.log(unitIndex);
              console.log(unitItems);
              console.log(unitSensor);
              return;
          }
          let unit = unitItems[unitIndex];
          commcaches.putCommCacheInput(new GetCommCache({
              unitId: unitSensor.unitId,
              typedId: unitSensor.typedId,
              clientId: unitSensor.clientId,
              sinceDate: fromDate,
              toTime: toDate,
              significantDifference: sensorTypeItem.significantTimeDifference
          }));
  
          let comms = commCacheMap[unitSensor.typedId];
  
          if (comms) {
              sortedUnitSensors.push(new SortedUnitSensor(
                  unitSensor,
                  unit,
                  comms
              ));
          } else {    
              return;
          }
  
        }
      ).then(() => {
        res(result);
      });
    });
  }

  private processCommCacheMap(
    chart: any,
    unitSensorItems: any[],
    unitItems: any,
    sensortypeItems: any,
    commcaches: any,
    commCacheMap: any,
    triggerableStats: any
  ) {
    const result: any = {};
    const sortedUnitSensors: any[] = result.sortedUnitSensors = [];

    return new Promise( (res, rej) => {

      let time = Number(chart.durationFrom);
  
      switch (chart.durationFromType) {
          case DurationFromType.days:
              time *= 24 * 60 * 60;
              break;
          case DurationFromType.hour:
              time *= 60 * 60;
              break;
          case DurationFromType.minute:
              time *= 60;
              break;
          default:
              break;
      }
      let fromTime = time * 1000;
      // this.duration = fromTime;
  
      time = Math.floor(Date.now() / 1000) - time;
  
      result.ts = (triggerableStats.getParsedItems || [])
        .filter(o => o.associatedChart === chart.typedId)
        .sort((a, b) => a.statIndex - b.statIndex);

      processArrayAsync(
        unitSensorItems,
        unitSensorItem =>  {
          let unitIndex = unitItems.findIndex(unitItem => 
              unitSensorItem.unitId === 
              unitItem.typedId.substring(0, unitSensorItem.unitId.length)
          );
          if (
              unitIndex < 0 || 
              unitIndex >= unitItems.length
          ) {
              console.error('Incorrect unitId');
              console.log(unitIndex);
              console.log(unitItems);
              console.log(unitSensorItem);
              return;
          }
          let unit = unitItems[unitIndex];
          let unitSensorAlias = unitSensorItem.typedId.split(':')[1];
          
          const sensorType = findSensorType(sensortypeItems, unitSensorAlias);
          
          if (
              !sensorType ||
              sensorType.typedId !== chart.sensorType
          ) {
              return;
          }
  
          commcaches.putCommCacheInput(new GetCommCache({
            unitId: unitSensorItem.unitId,
            typedId: unitSensorItem.typedId,
            clientId: unitSensorItem.clientId,
            fromTime,
            toPresent: true,
            significantDifference: sensorType.significantTimeDifference
          }));
          
          if (
              chart.includesUnitSensor(unit, unitSensorAlias, sensorType.typedId)
          ) {                
              let comms: any = commCacheMap[unitSensorItem.typedId];
              
              if (comms) {
                  sortedUnitSensors.push(new SortedUnitSensor(
                      unitSensorItem,
                      unit,
                      comms
                  ));
              } else {    
                  return;
              }
          }
      }).then(() => res(result));
    });
  }
}


// WEBPACK FOOTER //
// ./src/stores/application/classes/Chart.ts